import { useState, useEffect } from "react";
import { api } from "@/App";
import StaffLayout from "@/components/layout/StaffLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Users, Ticket, Car, AlertCircle, TrendingUp, Clock } from "lucide-react";
import { Link } from "react-router-dom";

const StaffDashboard = () => {
  const [stats, setStats] = useState(null);
  const [recentIssues, setRecentIssues] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [customersRes, issuesRes] = await Promise.all([
        api.get("/staff/customers?limit=5"),
        api.get("/issues?status=open")
      ]);
      setStats({
        totalCustomers: customersRes.data.total,
        openIssues: issuesRes.data.length
      });
      setRecentIssues(issuesRes.data.slice(0, 5));
    } catch (error) {
      console.error("Failed to fetch dashboard data");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <StaffLayout title="Dashboard">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[1,2,3,4].map(i => (
            <div key={i} className="h-32 rounded-xl animate-shimmer" />
          ))}
        </div>
      </StaffLayout>
    );
  }

  return (
    <StaffLayout title="Dashboard">
      <div className="space-y-6 animate-fade-in">
        {/* Quick Actions */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Link to="/staff/customers" className="stat-card card-hover" data-testid="staff-customers-card">
            <Users className="w-6 h-6 text-[#D4AF37] mb-3" />
            <p className="stat-value">{stats?.totalCustomers || 0}</p>
            <p className="stat-label">Customers</p>
          </Link>
          
          <Link to="/staff/vouchers" className="stat-card card-hover" data-testid="staff-vouchers-card">
            <Ticket className="w-6 h-6 text-[#4ADE80] mb-3" />
            <p className="stat-label">Validate</p>
            <p className="stat-value text-lg">Voucher</p>
          </Link>
          
          <Link to="/staff/rides" className="stat-card card-hover" data-testid="staff-rides-card">
            <Car className="w-6 h-6 text-[#60A5FA] mb-3" />
            <p className="stat-label">Create</p>
            <p className="stat-value text-lg">Ride</p>
          </Link>
          
          <Link to="/staff/issues" className="stat-card card-hover" data-testid="staff-issues-card">
            <AlertCircle className="w-6 h-6 text-[#F59E0B] mb-3" />
            <p className="stat-value">{stats?.openIssues || 0}</p>
            <p className="stat-label">Open Issues</p>
          </Link>
        </div>

        {/* Open Issues */}
        <Card className="bg-[#131316] border-[#2A2A2F]">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-white">Recent Open Issues</CardTitle>
            <Link to="/staff/issues" className="text-[#D4AF37] text-sm hover:underline">
              View all
            </Link>
          </CardHeader>
          <CardContent>
            {recentIssues.length > 0 ? (
              <div className="space-y-3">
                {recentIssues.map((issue) => (
                  <div key={issue.id} className="flex items-center justify-between py-3 border-b border-[#2A2A2F] last:border-0">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-[#F59E0B]/10 flex items-center justify-center">
                        <Clock size={18} className="text-[#F59E0B]" />
                      </div>
                      <div>
                        <p className="text-white font-medium">{issue.subject}</p>
                        <p className="text-[#6B6B6B] text-sm">{formatDate(issue.created_at)}</p>
                      </div>
                    </div>
                    <span className="badge badge-warning">Open</span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-[#6B6B6B]">
                <AlertCircle size={48} className="mx-auto mb-3 opacity-50" />
                <p>No open issues</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </StaffLayout>
  );
};

export default StaffDashboard;
